import { ObjectSchema } from "@hapi/joi";
import Joi from "fe-shared/src/helpers/validation";
import { startOfDay } from "date-fns";
import { ErrorMessages } from "fe-shared/src/types/common";
import { getNoOffsetDate } from "fe-shared/src/helpers/dates";
import {
  HaulerRequestFormData,
  RequestFormData,
  RequestTiresData,
} from "../../../types";

const MAX_TIRES_COUNT = 99999;

export const requestInitialContent: RequestFormData = {
  requestId: undefined,
  largeTireDetailsSelected: false,
  largeTireDetails: null,
  giantTireDetailsSelected: false,
  giantTireDetails: null,
  collectorName: "",
  collectorId: "",
  pickUpDateFrom: null,
  pickUpDateTo: null,
  processorName: "",
  processorId: "",
};

export const haulerInitialContent: HaulerRequestFormData = {
  haulerName: "",
  haulerId: "",
};

const requestErrorMessages: ErrorMessages = {
  collectorId: {
    "number.base": "validation.collectorRequired",
  },
  processorId: {
    "number.base": "validation.processorRequired",
  },
  pickUpDateFrom: {
    "date.base": "validation.dateRequired",
    "date.format": "validation.datePattern",
    "date.min": "validation.dateInFuture",
  },
  pickUpDateTo: {
    "date.base": "validation.dateRequired",
    "date.format": "validation.datePattern",
    "date.min": "validation.dateInFuture",
  },
  largeTireDetailsSelected: {
    "any.only": "validation.tireRequired",
  },
  giantTireDetailsSelected: {
    "any.only": "validation.tireRequired",
  },
  "largeTireDetails.quantity": {
    "number.base": "validation.quantityRequired",
    "number.min": "validation.quantityInvalid",
    "number.max": "validation.quantityInvalid",
    "number.unsafe": "validation.quantityInvalid",
  },
  "giantTireDetails.quantity": {
    "number.base": "validation.quantityRequired",
    "number.min": "validation.quantityInvalid",
    "number.max": "validation.quantityInvalid",
    "number.unsafe": "validation.quantityInvalid",
  },
};

const haulerErrorMessages: ErrorMessages = {
  haulerId: {
    "number.base": "validation.haulerRequired",
  },
};

const tiresValidationConfig: ObjectSchema<RequestTiresData> = Joi.object({
  quantity: Joi.number().integer().min(1).max(MAX_TIRES_COUNT).required(),
  make: Joi.string().max(256).allow("", null),
  model: Joi.string().max(256).allow("", null),
  dimensions: Joi.string().max(256).allow("", null),
});

const requestValidationConfig: ObjectSchema<RequestFormData> = Joi.object({
  requestId: Joi.number(),
  collectorId: Joi.number().required(),
  processorId: Joi.number().required(),
  pickUpDateFrom: Joi.date().min(getNoOffsetDate(startOfDay(new Date()))),
  pickUpDateTo: Joi.date().min(getNoOffsetDate(startOfDay(new Date()))),
  largeTireDetailsSelected: Joi.boolean().when("giantTireDetailsSelected", {
    is: Joi.boolean().valid(true),
    then: Joi.boolean(),
    otherwise: Joi.boolean().valid(true),
  }),
  largeTireDetails: Joi.any().when("largeTireDetailsSelected", {
    is: Joi.valid(true),
    then: tiresValidationConfig,
    otherwise: Joi.any().allow(null),
  }),
  giantTireDetailsSelected: Joi.boolean(),
  giantTireDetails: Joi.any().when("giantTireDetailsSelected", {
    is: Joi.valid(true),
    then: tiresValidationConfig,
    otherwise: Joi.any().allow(null),
  }),
}).unknown();

const haulerValidationConfig: ObjectSchema<HaulerRequestFormData> = Joi.object({
  haulerId: Joi.number().required(),
});

export const getFormConfig = (isStaffDialog: boolean) => {
  const initialContent = isStaffDialog
    ? { ...requestInitialContent, ...haulerInitialContent }
    : requestInitialContent;

  const errorMessages = isStaffDialog
    ? { ...requestErrorMessages, ...haulerErrorMessages }
    : requestErrorMessages;

  const validationConfig = isStaffDialog
    ? requestValidationConfig.concat(haulerValidationConfig)
    : requestValidationConfig;

  return {
    initialContent,
    errorMessages,
    validationConfig,
  };
};
